import React, {
  useEffect, useMemo, useState,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { getConfig } from '@edx/frontend-platform';
import { sendPageEvent, sendTrackEvent } from '@edx/frontend-platform/analytics';
import { Form, Spinner, StatefulButton } from '@openedx/paragon';
import classNames from 'classnames';
import PropTypes from 'prop-types';
import { Helmet } from 'react-helmet';
import Skeleton from 'react-loading-skeleton';
import { getCountryList, getLocale, useIntl } from '@edx/frontend-platform/i18n';
import ConfigurableRegistrationForm from './components/ConfigurableRegistrationForm';
import RegistrationFailure from './components/RegistrationFailure';
import {
  backupRegistrationFormBegin,
  clearRegistrationBackendError,
  registerNewUser,
  setEmailSuggestionInStore,
  setUserPipelineDataLoaded,
} from './data/actions';
import {
  FORM_SUBMISSION_ERROR,
  TPA_AUTHENTICATION_FAILURE,
} from './data/constants';
import getBackendValidations from './data/selectors';
import {
  isFormValid, prepareRegistrationPayload,
} from './data/utils';
import messages from './messages';
import {
  CountryField,
  EmailField,
  EstadoField,
  HonorCode,
  NameField,
  TermsOfService,
  UsernameField
} from './RegistrationFields';
import {
  InstitutionLogistration,
  PasswordField,
  RedirectLogistration,
  ThirdPartyAuthAlert,
} from '../common-components';
import { getThirdPartyAuthContext as getRegistrationDataFromBackend } from '../common-components/data/actions';
import EnterpriseSSO from '../common-components/EnterpriseSSO';
import ThirdPartyAuth from '../common-components/ThirdPartyAuth';
import {
  COMPLETE_STATE, PENDING_STATE, REGISTER_PAGE,
} from '../data/constants';
import {
  getAllPossibleQueryParams, getTpaHint, getTpaProvider, isHostAvailableInQueryParams, setCookie,
} from '../data/utils';
import CatalogoField from "./RegistrationFields/CatalogoField/CatalogoField";
import EresDocente from "./RegistrationFields/EresDocenteField/EresDocente";
import Cuentanos from "./RegistrationFields/CuentanosField/Cuentanos";
import TextoField from "./RegistrationFields/TextoField/TextoField";

/**
 * Main Registration Page component
 */
const RegistrationPage = (props) => {
  const { formatMessage } = useIntl();
  const dispatch = useDispatch();

  const registrationEmbedded = isHostAvailableInQueryParams();
  const platformName = getConfig().SITE_NAME;
  const flags = {
    showConfigurableEdxFields: getConfig().SHOW_CONFIGURABLE_EDX_FIELDS,
    showConfigurableRegistrationFields: false ,
    showMarketingEmailOptInCheckbox: getConfig().MARKETING_EMAILS_OPT_IN,
    autoGeneratedUsernameEnabled: getConfig().ENABLE_AUTO_GENERATED_USERNAME,
  };
  const {
    handleInstitutionLogin,
    institutionLogin,
  } = props;

  const backedUpFormData = useSelector(state => state.register.registrationFormData);
  const registrationError = useSelector(state => state.register.registrationError);
  const registrationErrorCode = registrationError?.errorCode;
  const registrationResult = useSelector(state => state.register.registrationResult);
  const shouldBackupState = useSelector(state => state.register.shouldBackupState);
  const userPipelineDataLoaded = useSelector(state => state.register.userPipelineDataLoaded);
  const submitState = useSelector(state => state.register.submitState);

  const fieldDescriptions = useSelector(state => state.commonComponents.fieldDescriptions);
  const optionalFields = useSelector(state => state.commonComponents.optionalFields);
  const thirdPartyAuthApiStatus = useSelector(state => state.commonComponents.thirdPartyAuthApiStatus);
  const autoSubmitRegForm = useSelector(state => state.commonComponents.thirdPartyAuthContext.autoSubmitRegForm);
  const thirdPartyAuthErrorMessage = useSelector(state => state.commonComponents.thirdPartyAuthContext.errorMessage);
  const finishAuthUrl = useSelector(state => state.commonComponents.thirdPartyAuthContext.finishAuthUrl);
  const currentProvider = useSelector(state => state.commonComponents.thirdPartyAuthContext.currentProvider);
  const providers = useSelector(state => state.commonComponents.thirdPartyAuthContext.providers);
  const secondaryProviders = useSelector(state => state.commonComponents.thirdPartyAuthContext.secondaryProviders);
  const pipelineUserDetails = useSelector(state => state.commonComponents.thirdPartyAuthContext.pipelineUserDetails);

  const backendValidations = useSelector(getBackendValidations);
  const queryParams = useMemo(() => getAllPossibleQueryParams(), []);
  const tpaHint = useMemo(() => getTpaHint(), []);

  const [formFields, setFormFields] = useState({ ...backedUpFormData.formFields });
  const [configurableFormFields, setConfigurableFormFields] = useState({ ...backedUpFormData.configurableFormFields });
  const [errors, setErrors] = useState({ ...backedUpFormData.errors });
  const [errorCode, setErrorCode] = useState({ type: '', count: 0 });
  const [formStartTime, setFormStartTime] = useState(null);
  // temporary error state for embedded experience because we don't want to show errors on blur
  const [temporaryErrors, setTemporaryErrors] = useState({ ...backedUpFormData.errors });

  const estadoList=useMemo(() =>{ return [
    {
      code: '1',
      name: 'Aguascalientes'
    },
    {
      code: '2',
      name: 'Baja California'
    },
    {
      code: '3',
      name: 'Baja California Sur'
    },
    {
      code: '4',
      name: 'Campeche'
    },
    {
      code: '7',
      name: 'Chiapas'
    },
    {
      code: '8',
      name: 'Chihuahua'
    },
    {
      code: '9',
      name: 'Ciudad de México'
    },
    {
      code: '5',
      name: 'Coahuila'
    },
    {
      code: '6',
      name: 'Colima'
    },
    {
      code: '10',
      name: 'Durango'
    },
    {
      code: '11',
      name: 'Guanajuato'
    },
    {
      code: '12',
      name: 'Guerrero'
    },
    {
      code: '13',
      name: 'Hidalgo'
    },
    {
      code: '14',
      name: 'Jalisco'
    },
    {
      code: '15',
      name: 'México'
    },
    {
      code: '16',
      name: 'Michoacán'
    },
    {
      code: '17',
      name: 'Morelos'
    },
    {
      code: '18',
      name: 'Nayarit'
    },
    {
      code: '19',
      name: 'Nuevo León'
    },
    {
      code: '20',
      name: 'Oaxaca'
    },
    {
      code: '21',
      name: 'Puebla'
    },
    {
      code: '22',
      name: 'Querétaro'
    },
    {
      code: '23',
      name: 'Quintana Roo'
    },
    {
      code: '24',
      name: 'San Luis Potosí'
    },
    {
      code: '25',
      name: 'Sinaloa'
    },
    {
      code: '26',
      name: 'Sonora'
    },

    {
      code:'27',
      name:'Tabasco'
    },
    {
      code: '28',
      name: 'Tamaulipas'
    },
    {
      code: '29',
      name: 'Tlaxcala'
    },
    {
      code: '30',
      name: 'Veracruz'

    },
    {
      code: '31',
      name: 'Yucatán'
    },
    {
      code: '32',
      name: 'Zacatecas'
    },
    {
      code:'33',
      name:'Fuera de México'
    }
  ]},[])

  const funcionList=useMemo(() =>{ return [
    {
      code: '0',
      name: 'DOCENTE FRENTE A GRUPO'
    },
    {
      code: '1',
      name: 'ADMINSTRATIVAS'
    },
    {
      code: '2',
      name: 'DIRECTIVAS'
    },
    {
      code: '5',
      name: 'SUPERVISIÓN'
    },
    {
      code: '6',
      name: 'ASESOR TÉCNICO PEDAGÓGICO'
    },
    {
      code: '3',
      name: 'TÉCNICAS'
    },
    {
      code: '4',
      name: 'OTRAS'
    }
  ]},[])


  const ocupacionList=useMemo(() =>{ return [
    {
      code: '1',
      name: 'ESTUDIANTE'
    },
    {
      code: '2',
      name: 'EDUCATIVO'
    },
    {
      code: '3',
      name: 'COMERCIAL'
    },
    {
      code: '4',
      name: 'GOBIERNO'
    },
      {
      code: '5',
      name: 'CULTURAL'
    },

      {
      code: '6',
      name: 'SALUD'
    },
      {
      code: '8',
        name: 'PRODUCCIÓN AUDIOVISUAL'
    }, {
      code: '7',
      name: 'OTRO'
    },


  ]},[])

  const countryList = useMemo(() => getCountryList(getLocale()).concat([{ code: 'US', name: 'United States' }]), []);

  const nivelList=useMemo(() =>{ return [

    {code:'0',name: "EDUCACIÓN PREESCOLAR"},
    {code:'1',name: "EDUCACIÓN PRIMARIA"},
    {code:'2',name: "EDUCACIÓN SECUNDARIA"},
    {code:'3',name: "EDUCACIÓN MEDIA SUPERIOR"},
    {code:'4',name: "EDUCACIÓN SUPERIOR"},
    {code:'5',name: "FORMACIÓN DOCENTE (ESCUELA NORMAL)"},
    {code:'6',name: "EDUCACIÓN ESPECIAL"},
    {code:'7',name: "EDUCACIÓN INDÍGENA"},
    {code:'8',name: "EDUCACIÓN PARA ADULTOS"},
    {code:'9',name: "CAPACITACIÓN PARA TRABAJO"}
  ]},[])

const maximoNivelList=useMemo(() =>{ return [
  {code:'0',  name: "Preescolar"},
  {code:'1',  name: "Primaria"},
  {code:'2',  name: "Secundaria"},
  {code:'3',  name: "Preparatoria o Bachillerato"},
  {code:'4',  name: "Técnica-Profesional"},
  {code:'5',  name: "Licenciatura"},
  {code:'6',  name: "Especialidad"},
  {code:'7',  name: "Maestría"},
  {code:'8',  name: "Doctorado"},
  {code:'9',  name: "Ninguna educación formal"}


]},[])



  const { cta, host } = queryParams;
  const buttonLabel = "Crear una cuenta";

  /**
   * Set the userPipelineDetails data in formFields for only first time
   */
  useEffect(() => {
    if (!userPipelineDataLoaded && thirdPartyAuthApiStatus === COMPLETE_STATE) {
      if (thirdPartyAuthErrorMessage) {
        setErrorCode(prevState => ({ type: TPA_AUTHENTICATION_FAILURE, count: prevState.count + 1 }));
      }
      if (pipelineUserDetails && Object.keys(pipelineUserDetails).length !== 0) {
        const { name = '', username = '', email = '' } = pipelineUserDetails;
        setFormFields(prevState => ({
          ...prevState, name, username, email,
        }));
        dispatch(setUserPipelineDataLoaded(true));
      }
    }
  }, [ // eslint-disable-line react-hooks/exhaustive-deps
    thirdPartyAuthApiStatus,
    thirdPartyAuthErrorMessage,
    pipelineUserDetails,
    userPipelineDataLoaded,
  ]);

  useEffect(() => {
    if (!formStartTime) {
      sendPageEvent('login_and_registration', 'register');
      const payload = { ...queryParams, is_register_page: true };
      if (tpaHint) {
        payload.tpa_hint = tpaHint;
      }
      dispatch(getRegistrationDataFromBackend(payload));
      setFormStartTime(Date.now());
    }
  }, [dispatch, formStartTime, queryParams, tpaHint]);

  /**
   * Backup the registration form in redux when register page is toggled.
   */
  useEffect(() => {
    if (shouldBackupState) {
      dispatch(backupRegistrationFormBegin({
        ...backedUpFormData,
        configurableFormFields: { ...configurableFormFields },
        formFields: { ...formFields },
        errors: { ...errors },
      }));
    }
  }, [shouldBackupState, configurableFormFields, formFields, errors, dispatch, backedUpFormData]);

  useEffect(() => {
    if (backendValidations) {
      if (registrationEmbedded) {
        setTemporaryErrors(prevErrors => ({ ...prevErrors, ...backendValidations }));
      } else {
        setErrors(prevErrors => ({ ...prevErrors, ...backendValidations }));
      }
    }
  }, [backendValidations, registrationEmbedded]);

  useEffect(() => {
    if (registrationErrorCode) {
      setErrorCode(prevState => ({ type: registrationErrorCode, count: prevState.count + 1 }));
    }
  }, [registrationErrorCode]);

  useEffect(() => {
    if (registrationResult.success) {
      // This event is used by GTM
      sendTrackEvent('edx.bi.user.account.registered.client', {});

      // This is used by the "User Retention Rate Event" on GTM
      setCookie(getConfig().USER_RETENTION_COOKIE_NAME, true);
    }
  }, [registrationResult]);

  const handleOnChange = (event) => {
    const { name } = event.target;
    const value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
    if (registrationError[name]) {
      dispatch(clearRegistrationBackendError(name));
    }
    setErrors(prevErrors => ({ ...prevErrors, [name]: '' }));

    if (name === 'nombres'||name === 'primer_apellido'||name === 'segundo_apellido') {
      setFormFields(prevState => ({ ...prevState, ['name']: formFields.nombres+' '+formFields.primer_apellido+' '+formFields.segundo_apellido }));
    }

    setFormFields(prevState => ({ ...prevState, [name]: value }));
    return;


  };
  const handleOnChangeEstado = (event, countryValue = null) => {
    const { name } = event.target;
    let value;
    if (countryValue) {
      value = { ...countryValue };
    } else {
      value = event.target.type === 'checkbox' ? event.target.checked : event.target.value;
      if (event.target.type === 'checkbox') {
        setFieldErrors(prevErrors => ({ ...prevErrors, [name]: '' }));
      }
    }
    if ( name ==='pais'){
      formFields.curp='XEXX010101HDFXXX04'
      formFields.municipio = 'FUERA DE MÉXICO'

    } else if (name ==='estado') {
      formFields.curp=''
      formFields.municipio = ''
    }
    setFormFields(prevState => ({ ...prevState, [name]: value }));
  };

  const handleErrorChange = (fieldName, error) => {
    console.log(error)
    if (registrationEmbedded) {
      setTemporaryErrors(prevErrors => ({
        ...prevErrors,
        [fieldName]: error,
      }));
      if (error === '' && errors[fieldName] !== '') {
        setErrors(prevErrors => ({
          ...prevErrors,
          [fieldName]: error,
        }));
      }
    } else {
      setErrors(prevErrors => ({
        ...prevErrors,
        [fieldName]: error,
      }));
    }
  };

  const registerUser = () => {
    const totalRegistrationTime = (Date.now() - formStartTime) / 1000;


    formFields.curp = formFields.curp.toUpperCase()

    formFields.nombres=formFields.nombres.toUpperCase()
    formFields.primer_apellido=formFields.primer_apellido.toUpperCase()
    formFields.segundo_apellido=formFields.segundo_apellido.toUpperCase()
    formFields.name=formFields.nombres+' '+formFields.primer_apellido+' '+formFields.segundo_apellido

    if (formFields.email==='') {
        return handleErrorChange('email', 'Correo es requerido');
    }
    if (formFields.nombres==='') {
        return handleErrorChange('nombres', 'Nombre es requerido');
    }
    if (formFields.primer_apellido==='') {
        return handleErrorChange('primer_apellido', 'Primer apellido es requerido');
    }

    if (parseInt(formFields.estado.estadoCode)>= 33){
      formFields.curp ="XEXX010101HDFXXX04"
      formFields.municipio ="FUERA DE MÉXICO"
    }



    let _payload = { ...formFields };


    if (formFields.estado && formFields.estado.estadoCode) {
      _payload.estado = formFields.estado.estadoCode;
    }else{
        return handleErrorChange('estado', 'Estado es requerido');
    }




    if (formFields.ocupacion&& formFields.ocupacion.catalogoCode) {
      _payload.ocupacion = formFields.ocupacion.catalogoCode;

    } else{
      return handleErrorChange('ocupacion', 'Ocupación es requerida');
    }






    if (formFields.maximo_nivel&& formFields.maximo_nivel.catalogoCode) {
      _payload.maximo_nivel = formFields.maximo_nivel.catalogoCode;
    }else{
        return handleErrorChange('maximo_nivel', 'Máximo nivel de estudios es requerido');
    }


    if (!formFields.tos){
        return handleErrorChange('tos', 'Debes aceptar los términos y condiciones');
    }

    if (!formFields.honor_code){
        return handleErrorChange('honor_code', 'Debes aceptar el código de honor');
    }

    if (formFields.eres_docente){
      if (formFields.cct){
        _payload.cct=formFields.cct.toUpperCase()
      }else{
        return handleErrorChange('cct', 'CCT es requerida');
      }

      if (formFields.funcion && formFields.funcion.catalogoCode) {
        _payload.funcion = formFields.funcion.catalogoCode;
      }else{
          return handleErrorChange('funcion', 'Función es requerida');
      }
      if (formFields.nivel_Educativo&& formFields.nivel_Educativo.catalogoCode) {
        _payload.nivel_Educativo = formFields.nivel_Educativo.catalogoCode;

      }else{
          return handleErrorChange('nivel_Educativo', 'Nivel Educativo es requerido');

      }
    } else{
      _payload.funcion='0'
      _payload.nivel_Educativo='0'
    }



    let payload = {...formFields};


    if (currentProvider) {
      delete payload.password;
      payload.social_auth_provider = currentProvider;
    }
    if (flags.autoGeneratedUsernameEnabled) {
      delete payload.username;
    }

    // Validating form data before submitting
    const { isValid, fieldErrors, emailSuggestion } = isFormValid(
      payload,
      registrationEmbedded ? temporaryErrors : errors,
      configurableFormFields,
      fieldDescriptions,
      formatMessage,
    );
    setErrors({ ...fieldErrors });

    dispatch(setEmailSuggestionInStore(emailSuggestion));
    console.log(fieldErrors)
    // returning if not valid
    if (!isValid) {
      setErrorCode(prevState => ({ type: FORM_SUBMISSION_ERROR, count: prevState.count + 1 }));
      return;
    }
  console.log(fieldErrors)
    // Preparing payload for submission
    payload = prepareRegistrationPayload(
      payload,
      configurableFormFields,
      flags.showMarketingEmailOptInCheckbox,
      totalRegistrationTime,
      queryParams);
    try{
        dispatch(registerNewUser(payload));
        console.log("no es aquí")
    } catch (e) {
      console.log(e)
    }


  };

  const handleSubmit = (e) => {
    e.preventDefault();
    registerUser();
  };

  useEffect(() => {
    if (autoSubmitRegForm && userPipelineDataLoaded) {
      registerUser();
    }
  }, [autoSubmitRegForm, userPipelineDataLoaded]); // eslint-disable-line react-hooks/exhaustive-deps

  const renderForm = () => {
    if (institutionLogin) {
      return (
        <InstitutionLogistration
          secondaryProviders={secondaryProviders}
          headingTitle={formatMessage(messages['register.institution.login.page.title'])}
        />
      );
    }

    return (
      <>
        <Helmet>
          <title>{formatMessage(messages['register.page.title'], { siteName: getConfig().SITE_NAME })}</title>
        </Helmet>
        <RedirectLogistration
          host={host}
          authenticatedUser={registrationResult.authenticatedUser}
          success={registrationResult.success}
          redirectUrl={registrationResult.redirectUrl}
          finishAuthUrl={finishAuthUrl}
          optionalFields={optionalFields}
          registrationEmbedded={registrationEmbedded}
          redirectToProgressiveProfilingPage={
            false
          }
        />
        {autoSubmitRegForm && !errorCode.type ? (
          <div className="mw-xs mt-5 text-center">
            <Spinner animation="border" variant="primary" id="tpa-spinner" />
          </div>
        ) : (
          <div
            className={classNames(
              'mw-xs mt-3',
              { 'w-100 m-auto pt-4 main-content': registrationEmbedded },
            )}
          >
            <ThirdPartyAuthAlert
              currentProvider={currentProvider}
              platformName={platformName}
              referrer={REGISTER_PAGE}
            />
            <RegistrationFailure
              errorCode={errorCode.type}
              failureCount={errorCode.count}
              context={{ provider: currentProvider, errorMessage: thirdPartyAuthErrorMessage }}
            />
            <Form id="registration-form" name="registration-form" autoComplete={"off"} >

              <TextoField

                  name="nombres"
                  value={formFields.nombres}
                  handleChange={handleOnChange}
                  handleErrorChange={handleErrorChange}
                  errorMessage={errors.nombres}
                  helpText={[formatMessage(messages['help.text.nombres'])]}
                  floatingLabel='Nombre'
                  autoComplete={"off"}
              />

              <TextoField
                  name="primer_apellido"
                  value={formFields.primer_apellido}
                  handleChange={handleOnChange}
                  handleErrorChange={handleErrorChange}
                  errorMessage={errors.primer_apellido}
                  helpText={[formatMessage(messages['help.text.primer_apellido'])]}
                  floatingLabel='Primer apellido'
                  autoComplete={"off"}
              />

              <TextoField
                  name="segundo_apellido"
                  value={formFields.segundo_apellido}
                  handleChange={handleOnChange}
                  handleErrorChange={handleErrorChange}
                  errorMessage={errors.segundo_apellido}
                  helpText={[formatMessage(messages['help.text.segundo_apellido'])]}
                  floatingLabel='Segundo apellido'
                  autoComplete={"off"}
              />

              <EstadoField
                  estadoList={estadoList}
                  selectedEstado={formFields.estado}
                  helpText={[formatMessage(messages['help.text.estado'])]}
                  errorMessage={errors.estado || ''}
                  onChangeHandler={handleOnChangeEstado}
                  handleErrorChange={handleErrorChange}
                  onBlurHandler={()=>{}}
                  onFocusHandler={()=>{}}
                  target={'estado'}
                  autoComplete={"off"}
              />
              { formFields.estado&& parseInt(formFields.estado.estadoCode) <= 32 &&(
              <TextoField
                  name="curp"
                  value={formFields.curp}
                  handleChange={handleOnChange}
                  handleErrorChange={handleErrorChange}
                  errorMessage={errors.curp}
                  helpText={[formatMessage(messages['help.text.curp'])]}
                  floatingLabel='C.U.R.P.'
                  autoComplete={"off"}
              />
                )}



              <EmailField
                name="email"
                value={formFields.email}
                confirmEmailValue={configurableFormFields?.confirm_email}
                handleErrorChange={handleErrorChange}
                handleChange={handleOnChange}
                errorMessage={errors.email}
                helpText={[formatMessage(messages['help.text.email'])]}
                floatingLabel={formatMessage(messages['registration.email.label'])}
                autoComplete={"off"}
              />



              { formFields.estado&& parseInt(formFields.estado.estadoCode)<= 32 &&(

                  <TextoField
                      name="municipio"
                      value={formFields.municipio}
                      handleChange={handleOnChange}
                      handleErrorChange={handleErrorChange}
                      errorMessage={errors.municipio}
                      helpText={[formatMessage(messages['help.text.municipio'])]}
                      floatingLabel='Municipio/Alcaldía'
                      autoComplete={"off"}
                  />




            ) }
              { formFields.estado&& parseInt(formFields.estado.estadoCode)> 32 &&(

                <div>
                  <EstadoField
                      estadoList={countryList}
                      selectedEstado={formFields.pais}
                      helpText={[formatMessage(messages['help.text.pais'])]}
                      errorMessage={errors.pais || ''}
                      onChangeHandler={handleOnChangeEstado}
                      handleErrorChange={handleErrorChange}
                      onBlurHandler={()=>{}}
                      onFocusHandler={()=>{}}
                      target={'pais'}
                      autoComplete={"off"}
                  />
                  <TextoField
                      name="dni"
                      value={formFields.dni}
                      handleChange={handleOnChange}
                      handleErrorChange={handleErrorChange}
                      errorMessage={errors.dni}
                      helpText={[formatMessage(messages['help.text.dni'])]}
                      floatingLabel='ID / Cédula de Identidad / DNI '
                      autoComplete={"off"}
                  />


                </div>

            ) }






              {!flags.autoGeneratedUsernameEnabled && (
                <UsernameField
                  name="username"
                  spellCheck="false"
                  value={formFields.username}
                  handleChange={handleOnChange}
                  handleErrorChange={handleErrorChange}
                  errorMessage={errors.username}
                  helpText={[formatMessage(messages['help.text.username.1']), formatMessage(messages['help.text.username.2'])]}
                  floatingLabel={formatMessage(messages['registration.username.label'])}
                  autoComplete={"off"}

                />
              )}
              {!currentProvider && (
                <PasswordField
                  name="password"
                  value={formFields.password}
                  handleChange={handleOnChange}
                  handleErrorChange={handleErrorChange}
                  errorMessage={errors.password}
                  floatingLabel={formatMessage(messages['registration.password.label'])}
                  autoComplete={"off"}
                />
              )}

              <CatalogoField
                  catalogoList={maximoNivelList}
                  selectedCatalogo={formFields.maximo_nivel}
                  helpText={[formatMessage(messages['help.text.maximo_nivel'])]}
                  errorMessage={errors.maximo_nivel || ''}
                  onChangeHandler={handleOnChangeEstado}
                  handleErrorChange={handleErrorChange}
                  onBlurHandler={()=>{}}
                  onFocusHandler={()=>{}}
                  target={'maximo_nivel'}
              />

              <CatalogoField
                  catalogoList={ocupacionList}
                  selectedCatalogo={formFields.ocupacion}
                  helpText={[formatMessage(messages['help.text.ocupacion'])]}
                  errorMessage={errors.ocupacion || ''}
                  onChangeHandler={handleOnChangeEstado}
                  handleErrorChange={handleErrorChange}
                  onBlurHandler={()=>{}}
                  onFocusHandler={()=>{}}
                  target={'ocupacion'}
              />
              <br/>
              <EresDocente onChangeHandler={handleOnChange} value={formFields.eres_docente} errorMessage={errors.eres_docente} />


              { formFields.eres_docente &&(
                  <TextoField
                      name="cct"
                      value={formFields.cct}
                      handleChange={handleOnChange}
                      handleErrorChange={handleErrorChange}
                      errorMessage={errors.cct}
                      helpText={[formatMessage(messages['help.text.cct'])]}
                      floatingLabel='Clave del Centro de Trabajo CCT'

                  />
                  )}


              { formFields.eres_docente &&(
                  <CatalogoField
                      catalogoList={funcionList}
                      selectedCatalogo={formFields.funcion}
                      helpText={[formatMessage(messages['help.text.funcion'])]}
                      errorMessage={errors.funcion || ''}
                      onChangeHandler={handleOnChangeEstado}
                      handleErrorChange={handleErrorChange}
                      onBlurHandler={()=>{}}
                      onFocusHandler={()=>{}}
                      target={'funcion'}
                  />
                )}
              { formFields.eres_docente &&(
                  <CatalogoField
                      catalogoList={nivelList}
                      selectedCatalogo={formFields.nivel_Educativo}
                      helpText={[formatMessage(messages['help.text.nivel_Educativo'])]}
                      errorMessage={errors.nivel_Educativo || ''}
                      onChangeHandler={handleOnChangeEstado}
                      handleErrorChange={handleErrorChange}
                      onBlurHandler={()=>{}}
                      onFocusHandler={()=>{}}
                      target={'nivel_Educativo'}
                  />
                  )}
              { formFields.eres_docente &&(
                    <TextoField
                        name="asignatura"
                        value={formFields.asignatura}
                        handleChange={handleOnChange}
                        handleErrorChange={handleErrorChange}
                        errorMessage={errors.asignatura}
                        helpText={[formatMessage(messages['help.text.asignatura'])]}
                        floatingLabel='Campo formativo'
                    />
                )

              }

              <ConfigurableRegistrationForm
                email={formFields.email}
                fieldErrors={errors}
                formFields={configurableFormFields}
                setFieldErrors={registrationEmbedded ? setTemporaryErrors : setErrors}
                setFormFields={setConfigurableFormFields}
                autoSubmitRegisterForm={autoSubmitRegForm}
                fieldDescriptions={fieldDescriptions}
              />
              <Cuentanos onChangeHandler={handleOnChange} value={formFields.cuentanos} errorMessage={errors.cuentanos} />

              <HonorCode value={formFields.honor_code} onChangeHandler={handleOnChange} errorMessage={errors.honor_code} />
              <TermsOfService onChangeHandler={handleOnChange} value={formFields.tos} errorMessage={errors.tos} />


              <StatefulButton
                id="register-user"
                name="register-user"
                type="submit"
                variant="brand"
                className="register-button mt-4 mb-4"
                state={submitState}
                labels={{
                  default: buttonLabel,
                  pending: '',
                }}
                onClick={handleSubmit}
                onMouseDown={(e) => e.preventDefault()}
              />
              {!registrationEmbedded && (
                <ThirdPartyAuth
                  currentProvider={currentProvider}
                  providers={providers}
                  secondaryProviders={secondaryProviders}
                  handleInstitutionLogin={handleInstitutionLogin}
                  thirdPartyAuthApiStatus={thirdPartyAuthApiStatus}
                />
              )}
            </Form>
          </div>
        )}

      </>
    );
  };

  if (tpaHint) {
    if (thirdPartyAuthApiStatus === PENDING_STATE) {
      return <Skeleton height={36} />;
    }
    const { provider, skipHintedLogin } = getTpaProvider(tpaHint, providers, secondaryProviders);
    if (skipHintedLogin) {
      window.location.href = getConfig().LMS_BASE_URL + provider.registerUrl;
      return null;
    }
    return provider ? <EnterpriseSSO provider={provider} /> : renderForm();
  }
  return (
    renderForm()
  );
};

RegistrationPage.propTypes = {
  institutionLogin: PropTypes.bool,
  // Actions
  handleInstitutionLogin: PropTypes.func,
};

RegistrationPage.defaultProps = {
  handleInstitutionLogin: null,
  institutionLogin: false,
};

export default RegistrationPage;
